import posthog from 'posthog-js';
import {
  readSessionItem,
  removeSessionItem,
  writeSessionItem,
} from 'lib/safeSessionStorage';

const NON_PRODUCTION_ENVS = new Set([
  'development',
  'review',
  'preprod',
  'compliance-staging',
  'staging',
  'test',
]);

const PRODUCTION_HOST_SUFFIX = ['my', 'alex', 'health', '.', 'com'].join('');
const PRODUCTION_HOSTS = new Set([
  `www.${PRODUCTION_HOST_SUFFIX}`,
  `alex-medicare-production.${PRODUCTION_HOST_SUFFIX}`,
  PRODUCTION_HOST_SUFFIX,
]);

/**
 * Custom events should only reach the production PostHog project.
 *
 * `NEXT_PUBLIC_*` is inlined at build time. The default-branch image is also
 * deployed to preprod, so capture always requires a production hostname —
 * never the build-time `production` flag alone.
 */
export function shouldCapture() {
  if (typeof window === 'undefined') return false;

  const env = process.env.NEXT_PUBLIC_SENTRY_CLIENT_ENVIRONMENT;
  if (env && NON_PRODUCTION_ENVS.has(env)) return false;

  return PRODUCTION_HOSTS.has(window.location.hostname);
}

/** Custom events the production PostHog project is expected to receive. */
export const ANALYTICS_EVENTS = {
  closedCaptionsEnabled: 'closed_captions_enabled',
  healthcareVisitsFormViewed: 'healthcare_visits_form_viewed',
  healthcareVisitsFormCompleted: 'healthcare_visits_form_completed',
  pharmacyFormViewed: 'pharmacy_form_viewed',
  pharmacyFormCompleted: 'pharmacy_form_completed',
  pharmacySearchPerformed: 'pharmacy_search_performed',
  pharmacySaved: 'pharmacy_saved',
  planBrowseStarted: 'plan_browse_started',
  planSaved: 'plan_saved',
  planSessionMode: 'plan_session_mode',
  planAddedToCart: 'plan_added_to_cart',
  prescriptionSaved: 'prescription_saved',
  prescriptionSearchPerformed: 'prescription_search_performed',
  providersFormViewed: 'providers_form_viewed',
  providersFormCompleted: 'providers_form_completed',
  providerSearchPerformed: 'provider_search_performed',
  providerSaved: 'provider_saved',
  rxFormViewed: 'rx_form_viewed',
  rxFormCompleted: 'rx_form_completed',
  conversationStarted: 'conversation_started',
  locationFormCompleted: 'location_form_completed',
  incomeFormCompleted: 'income_form_completed',
  sepFormCompleted: 'sep_form_completed',
  recommendationRequested: 'recommendation_requested',
  recommendationViewed: 'recommendation_viewed',
  dsRecommendationReached: 'ds_recommendation_reached',
  gapPdpMissingPdp: 'gap_pdp_missing_pdp',
  cartReviewViewed: 'cart_review_viewed',
  enrollmentStarted: 'enrollment_started',
  enrollmentFailed: 'enrollment_failed',
  enrollmentSubmitted: 'enrollment_submitted',
  sunfireCtaClicked: 'sunfire_cta_clicked',
  sunfireApplicationStarted: 'sunfire_application_started',
  accountSignupStarted: 'account_signup_started',
  accountCreated: 'account_created',
  userLoggedIn: 'user_logged_in',
  mailingListSignup: 'mailing_list_signup',
  toolkitSignup: 'toolkit_signup',
  plannerDownloaded: 'planner_downloaded',
} as const;

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export function capture(
  event: AnalyticsEvent | string,
  properties?: Record<string, unknown>
) {
  if (!shouldCapture()) return;
  posthog.capture(event, properties);
}

export function capturePlanBrowseStarted(entrySource: string) {
  capture(ANALYTICS_EVENTS.planBrowseStarted, {
    entry_source: entrySource,
  });
  capture(ANALYTICS_EVENTS.planSessionMode, { mode: 'browse' });
}

export function captureConversationStarted(entrySource: string) {
  capture(ANALYTICS_EVENTS.conversationStarted, {
    entry_source: entrySource,
  });
  capture(ANALYTICS_EVENTS.planSessionMode, { mode: 'conversation' });
}

export function capturePlanAddedToCart(properties?: {
  plan_type?: string;
  plan_id?: string;
  context?: string;
}) {
  capture(ANALYTICS_EVENTS.planAddedToCart, properties);
}

export function captureEnrollmentStarted(properties?: {
  plan_type?: string;
  plan_id?: string;
}) {
  capture(ANALYTICS_EVENTS.enrollmentStarted, properties);
  capture(ANALYTICS_EVENTS.sunfireCtaClicked, properties);
}

export function captureEnrollmentFailed(properties?: {
  plan_type?: string;
  plan_id?: string;
  status?: number;
  reason?: string;
}) {
  capture(ANALYTICS_EVENTS.enrollmentFailed, properties);
}

export function captureSunfireApplicationStarted(properties?: {
  plan_type?: string;
  plan_id?: string;
}) {
  capture(ANALYTICS_EVENTS.sunfireApplicationStarted, properties);
  capture(ANALYTICS_EVENTS.enrollmentSubmitted, properties);
}

const PENDING_SIGNUP_KEY = 'mah_pending_account_signup';
const USER_LOGGED_IN_KEY = 'mah_user_logged_in';
const RECOMMENDATION_REACHED_KEY = 'mah_recommendation_reached';

export function captureAccountSignupStarted(entrySource: string) {
  if (typeof window !== 'undefined') {
    writeSessionItem(PENDING_SIGNUP_KEY, entrySource);
  }
  capture(ANALYTICS_EVENTS.accountSignupStarted, {
    entry_source: entrySource,
  });
}

/** Auth0 `sub` looks like `provider|id`; return the provider segment when present. */
export function authMethodFromSub(sub: unknown): string | undefined {
  if (typeof sub !== 'string' || !sub.includes('|')) return undefined;
  return sub.split('|')[0];
}

/** Clear per-tab auth analytics markers on logout so a later login can emit again. */
export function clearAuthAnalyticsSession() {
  if (typeof window === 'undefined') return;
  removeSessionItem(USER_LOGGED_IN_KEY);
}

/**
 * Call after a successful Auth0 identify to record login / new account.
 * `user_logged_in` is once per browser tab session; refresh / remount does not
 * re-emit. `account_created` still requires a pending signup CTA marker.
 */
export function captureAuthSuccess(authMethod?: string) {
  const properties = authMethod ? { auth_method: authMethod } : undefined;
  const pendingSignup =
    typeof window !== 'undefined' ? readSessionItem(PENDING_SIGNUP_KEY) : null;

  if (pendingSignup) {
    removeSessionItem(PENDING_SIGNUP_KEY);
    capture(ANALYTICS_EVENTS.accountCreated, {
      ...properties,
      signup_source: pendingSignup,
    });
  }

  if (typeof window !== 'undefined') {
    if (readSessionItem(USER_LOGGED_IN_KEY)) return;
    writeSessionItem(USER_LOGGED_IN_KEY, '1');
  }

  capture(ANALYTICS_EVENTS.userLoggedIn, properties);
}

/**
 * Fire recommendation viewed/reached once per recommendation type per tab.
 * Safe to call from conversation routing and from cart RecSummary.
 */
export function captureRecommendationReached(recommendationType: string) {
  if (typeof window !== 'undefined') {
    const key = `${RECOMMENDATION_REACHED_KEY}:${recommendationType}`;
    if (readSessionItem(key)) return;
    writeSessionItem(key, '1');
  }

  capture(ANALYTICS_EVENTS.recommendationViewed, {
    recommendation_type: recommendationType,
  });
  capture(ANALYTICS_EVENTS.dsRecommendationReached, {
    recommendation_type: recommendationType,
  });
}

/**
 * Fire after a recommendation fetch returns a Medicare Supplement and no
 * Part D plan. Each fetch can emit again. Call only from `useRecommendation`.
 */
export function captureGapPdpMissingPdp(missingPlanYears: number[]) {
  const years = [...new Set(missingPlanYears)]
    .filter((year) => Number.isInteger(year))
    .sort((a, b) => a - b);

  if (years.length === 0) return;

  capture(ANALYTICS_EVENTS.gapPdpMissingPdp, {
    missing_plan_years: years,
  });
}

/** Newsletter / toolkit signup from the shared mailing-list form. */
export function captureMailingListSignup(pageName?: string, pageUri?: string) {
  const signupSource = pageName || pageUri || 'unknown';
  capture(ANALYTICS_EVENTS.mailingListSignup, {
    signup_source: signupSource,
    page_name: pageName,
    page_uri: pageUri,
  });
  if (pageUri === '/' || pageName === 'Home') {
    capture(ANALYTICS_EVENTS.toolkitSignup, {
      signup_source: signupSource,
    });
  }
}

export function secondsSince(mountTime: number | null): number {
  return Math.round((Date.now() - (mountTime ?? Date.now())) / 1000);
}
